/**
 * hk_os · <VoiceInstructionBar>
 * ────────────────────────────────────────────────────────────────────
 * Always-visible operational bar carrying the current voice instruction.
 * Layout: icon · Bengali text (from OA) · replay button · progress bar.
 *
 * Props:
 *   voiceKey      — required snippet key
 *   taskIcon      — optional lucide icon component (e.g. Sparkles)
 *   progress      — 0..1 · task progress (rendered as a linear bar)
 *   autoPlay      — auto-play on mount / voiceKey change (default true)
 *   variant       — "default" | "compact" | "hero"
 *   testid
 */
import React, { useEffect, useState } from "react";
import { Volume2 } from "lucide-react";
import { VoicePlayer } from "./VoicePlayer";
import { useVoiceRegistry } from "./useVoiceRegistry";

export function VoiceInstructionBar({
  voiceKey,
  taskIcon: TaskIcon = Volume2,
  progress = 0,
  autoPlay = true,
  variant = "default",
  testid,
}) {
  const { getSnippet } = useVoiceRegistry();
  const snippet = getSnippet(voiceKey);
  // Nudge the child <VoicePlayer> to re-autoplay when the key changes.
  const [autoplayNonce, setAutoplayNonce] = useState(0);
  useEffect(() => {
    setAutoplayNonce((n) => n + 1);
  }, [voiceKey]);

  const heightCls = variant === "compact" ? "min-h-[64px]"
                    : variant === "hero" ? "min-h-[104px]"
                    : "min-h-[80px]";
  const textCls   = variant === "hero" ? "text-[22px]"
                    : variant === "compact" ? "text-[15px]"
                    : "text-[18px]";
  const pct = Math.max(0, Math.min(1, progress));

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testid || "voice-instruction-bar"}
      className={[
        "sticky top-0 z-20 w-full border-b border-sky-200",
        "bg-gradient-to-r from-sky-50 via-white to-sky-50",
        "px-4 sm:px-6 py-3 flex items-center gap-3",
        heightCls,
      ].join(" ")}
    >
      <span className="shrink-0 rounded-full bg-sky-600 text-white p-2.5"
             aria-hidden>
        <TaskIcon className="w-5 h-5" strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <div
          data-testid={testid ? `${testid}-text` : "voice-instruction-text"}
          className={`${textCls} font-semibold text-slate-900 truncate`}
          lang={snippet?.locale || "bn-IN"}
        >
          {snippet?.text || "\u00A0"}
        </div>
        {/* Task progress rail (always rendered so height stays stable). */}
        <div
          aria-label="Task progress"
          className="mt-1.5 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden"
        >
          <div
            data-testid={testid ? `${testid}-progress` : "voice-instruction-progress"}
            style={{ width: `${pct * 100}%` }}
            className="h-full bg-sky-500 transition-[width] duration-300"
          />
        </div>
      </div>
      {/* Replay button — ALWAYS visible per ADR §22. */}
      <VoicePlayer
        key={autoplayNonce}
        voiceKey={voiceKey}
        autoPlay={autoPlay}
        size="md"
        showText={false}
        testid={testid ? `${testid}-player` : "voice-instruction-player"}
      />
    </div>
  );
}

export default VoiceInstructionBar;
